import { useState } from "react";
import { Search, Link2, PenTool, ChevronDown, Flag, MapPin, Heart, Plus, X, Edit2 } from "lucide-react";
import leopardImage from "../../assets/leopard.webp";
import elephantImage from "../../assets/elephant.webp";
import leopardTreeImage from "../../assets/leopard-tree.webp";
import birdImage from "../../assets/bird.webp";
import safariVehicleImage from "../../assets/safari-vehicle.webp";

export function ItemManagerCard() {
	const [activeTab, setActiveTab] = useState<"search" | "link" | "manual">("manual");
	const [showPhotoModal, setShowPhotoModal] = useState(false);

	// Sample photo gallery
	const samplePhotos = [leopardImage, elephantImage, leopardTreeImage, birdImage, safariVehicleImage];

	return (
		<>
			{/* Photo Upload Modal */}
			{showPhotoModal && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center"
					style={{
						background: "rgba(55, 75, 85, 0.85)",
						backdropFilter: "blur(4px)",
					}}
					onClick={() => setShowPhotoModal(false)}
				>
					<div className="bg-white rounded-3xl shadow-2xl p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
						{/* Upload Area */}
						<div
							className="border-2 border-dashed rounded-2xl p-10 mb-5 text-center transition-all hover:border-gray-400 hover:bg-gray-50 cursor-pointer"
							style={{ borderColor: "#D1D5DB" }}
						>
							<div className="flex flex-col items-center gap-3">
								<div
									className="w-14 h-14 rounded-2xl flex items-center justify-center"
									style={{
										background: "linear-gradient(135deg, #92501E 0%, #704214 100%)",
									}}
								>
									<svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
										<circle cx="12" cy="13" r="4" />
										<line x1="12" y1="11" x2="12" y2="15" />
										<line x1="10" y1="13" x2="14" y2="13" />
									</svg>
								</div>
								<p className="text-lg font-semibold text-gray-700">Click to upload or drop photos here</p>
							</div>
						</div>

						{/* Photo Thumbnails */}
						<div className="flex gap-2.5 mb-5 overflow-x-auto pb-2">
							{samplePhotos.map((photo, idx) => (
								<div
									key={idx}
									className="relative w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 group cursor-pointer hover:ring-2 hover:ring-offset-2 transition-all"
									style={{ ["--tw-ring-color" as string]: "#704214" }}
								>
									<img src={photo} alt="" className="w-full h-full object-cover" />
									<div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity" />
									<button className="absolute top-1 right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-gray-100">
										<X className="w-3.5 h-3.5 text-gray-600" />
									</button>
								</div>
							))}
						</div>

						{/* Save Button */}
						<div className="flex justify-center">
							<button
								className="px-10 py-3 rounded-2xl text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:scale-105"
								style={{
									background: "linear-gradient(135deg, #92501E 0%, #704214 100%)",
								}}
								onClick={() => setShowPhotoModal(false)}
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}

			<div
				className="w-[550px] rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:scale-105 relative"
				style={{
					border: "1px solid #e5e7eb",
				}}
			>
				{/* Number badge */}
				<div
					className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md z-10"
					style={{
						background: "linear-gradient(135deg, #C084FC 0%, #A78BFA 100%)",
					}}
				>
					4
				</div>

				{/* Top section with off-white background */}
				<div className="pt-16 px-6 pb-4" style={{ background: "#F8FAFB" }}>
					<h3 className="text-2xl font-bold mb-2 text-gray-900">Edit content</h3>
					<p className="text-gray-500 text-xs mb-4">Add a city, landmark, or hidden gem to your itinerary.</p>

					{/* Tabs */}
					<div className="flex gap-1.5 mb-4">
						<button
							onClick={() => setActiveTab("search")}
							className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
								activeTab === "search" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50"
							}`}
						>
							<Search className="w-3.5 h-3.5" />
							Search
						</button>
						<button
							onClick={() => setActiveTab("link")}
							className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
								activeTab === "link" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50"
							}`}
						>
							<Link2 className="w-3.5 h-3.5" />
							Add through link
						</button>
						<button
							onClick={() => setActiveTab("manual")}
							className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
								activeTab === "manual" ? "bg-[#0A4A4A] text-white" : "text-gray-600 hover:bg-gray-50"
							}`}
						>
							<PenTool className="w-3.5 h-3.5" />
							Add manually
						</button>
					</div>

					{/* POI Card */}
					<div className="mb-4 flex gap-3">
						{/* Image with Edit Button */}
						<div className="w-36 h-24 rounded-lg overflow-hidden flex-shrink-0 relative group">
							<img src={leopardImage} alt="Leopard" className="w-full h-full object-cover" />
							{/* Edit Button Overlay */}
							<button
								onClick={() => setShowPhotoModal(true)}
								className="absolute top-1.5 right-1.5 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 hover:shadow-xl z-10"
								style={{
									animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
								}}
							>
								<Edit2 className="w-4 h-4 text-gray-700" />
							</button>
						</div>

						{/* Info */}
						<div className="flex-1 flex flex-col">
							<div className="flex items-center gap-1.5 mb-2">
								<button className="px-2.5 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
									<Flag className="w-3.5 h-3.5 text-gray-700" />
								</button>
								<button className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs border border-gray-100 hover:bg-gray-50 transition-colors bg-white shadow-sm">
									<div className="w-2 h-2 rounded-full" style={{ background: "#A67C6D" }} />
									<span className="text-gray-900 font-medium">See &amp; Do</span>
									<ChevronDown className="w-3.5 h-3.5 text-gray-500" />
								</button>
							</div>

							<h4 className="font-bold text-base text-gray-900 mb-1.5">Yala National Park</h4>

							<p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
								Sri Lanka's most famous wildlife park, known for having one of the highest leopard densities in the world. The mix of forest, grassland, and
								lagoons makes it ideal for spotting elephants, crocodiles, and a huge variety of birds on safari.
							</p>
						</div>
					</div>
				</div>

				{/* Bottom section with white background */}
				<div className="px-6 pb-6 bg-white">
					{/* Lists */}
					<div className="mb-3">
						<h5 className="text-xs font-semibold text-gray-900 mb-2">Lists</h5>
						<div className="flex flex-wrap gap-1.5">
							<span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-md text-xs text-gray-700">
								🐘 National Parks / Safaris
								<button className="hover:bg-gray-200 rounded p-0.5">
									<X className="w-2.5 h-2.5" />
								</button>
							</span>
							<span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-md text-xs text-gray-700">
								<Heart className="w-3 h-3 fill-red-500 text-red-500" />
								Favorites
								<button className="hover:bg-gray-200 rounded p-0.5">
									<X className="w-2.5 h-2.5" />
								</button>
							</span>
							<button className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md">
								Add more <Plus className="w-3 h-3" />
							</button>
						</div>
					</div>

					{/* Address & Status Row */}
					<div className="grid grid-cols-2 gap-3 mb-3">
						<div>
							<h5 className="text-xs font-semibold text-gray-900 mb-1.5">Address</h5>
							<div className="flex items-center gap-1.5 text-gray-600 text-xs">
								<MapPin className="w-3.5 h-3.5" />
								Sri Lanka
							</div>
						</div>
						<div>
							<h5 className="text-xs font-semibold text-gray-900 mb-1.5">Status</h5>
							<button className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-100 rounded-md text-xs text-gray-700 hover:bg-gray-50 w-full justify-between">
								<div className="flex items-center gap-1.5">
									<Heart className="w-3.5 h-3.5" />
									Wishlist
								</div>
								<ChevronDown className="w-3.5 h-3.5" />
							</button>
						</div>
					</div>

					{/* Tags */}
					<div className="mb-3">
						<h5 className="text-xs font-semibold text-gray-900 mb-2">Tags</h5>
						<div className="flex flex-wrap gap-1.5">
							<span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-md text-xs text-gray-700">
								national park
								<button className="hover:bg-gray-200 rounded p-0.5">
									<X className="w-2.5 h-2.5" />
								</button>
							</span>
							<span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-md text-xs text-gray-700">
								wildlife park
								<button className="hover:bg-gray-200 rounded p-0.5">
									<X className="w-2.5 h-2.5" />
								</button>
							</span>
							<button className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md">
								Add tag <Plus className="w-3 h-3" />
							</button>
						</div>
					</div>

					{/* URL */}
					<div className="mb-4">
						<h5 className="text-xs font-semibold text-gray-900 mb-1.5">URL</h5>
						<div className="text-xs text-blue-600 hover:underline truncate">https://www.yalasrilanka.lk/</div>
					</div>

					{/* Update Button */}
					<div className="flex justify-end">
						<button
							className="px-6 py-2.5 rounded-xl text-white font-medium shadow-md hover:shadow-lg transition-shadow"
							style={{
								background: "linear-gradient(135deg, #92501E 0%, #704214 100%)",
							}}
						>
							Update Place
						</button>
					</div>
				</div>
			</div>
		</>
	);
}
